import type { FunctionComponent } from 'preact';
import type { UnsupportedErrorEvent } from './useErrorEvents.js';
import { Button } from '../Button/Button.js';
import { useCloudpack } from '../CloudpackProvider/CloudpackProvider.js';
import {
  ResultsAccordion,
  ResultsAccordionContent,
  type ResultsAccordionProps,
} from '../ResultsAccordion/ResultsAccordion.js';

/**
 * Item (collapsible section) in the list of path errors in the ErrorDialog.
 */
export const PathError: FunctionComponent<UnsupportedErrorEvent & Pick<ResultsAccordionProps, 'index'>> = (props) => {
  const { packageName, importPath, issuerUrl, fixable, isValidPackage } = props;
  const cloudpack = useCloudpack();
  const prettyImport = (
    <code>
      {packageName}
      {importPath === '.' ? '' : `/${importPath.replace(/^\.\//, '')}`}
    </code>
  );
  const prettyPackage = <code>{packageName}</code>;
  const isTrailingSlash = importPath.endsWith('/');

  const addOverride = () => {
    void cloudpack.addPackageOverride.mutate({ packageName, importPath, issuerUrl, initiatedBy: 'user' });
  };

  const getContent = () => {
    return (
      <>
        <ResultsAccordionContent
          itemName="Problem"
          itemValue={
            <>
              {isValidPackage ? (
                <>
                  {prettyImport} was imported, but <code>{importPath}</code> is not listed in {prettyPackage}&apos;s
                  exports map{fixable ? '' : ' and does not appear to exist on disk'}.
                </>
              ) : (
                <>An unrecognized package {prettyPackage} was imported.</>
              )}
              {issuerUrl && (
                <>
                  <br />
                  The bad import request came from:{' '}
                  <a href={issuerUrl} target="_blank" rel="noopener noreferrer">
                    {issuerUrl}
                  </a>
                </>
              )}
            </>
          }
        />
        <ResultsAccordionContent
          itemName="Possible solutions"
          itemValue={
            fixable ? (
              <>
                <p>
                  Click <strong>Add override</strong> above to add an exports map entry for this path in your {"app's "}
                  <code>cloudpack.generated.json</code>.
                </p>
                <p>
                  To automatically make updates like this in the future, add the following to your {"app's "}
                  <code>cloudpack.config.json</code>:
                  <br />
                  <code>{'{ "features": { "autoUpdateEntries": true } }'}</code>
                </p>
              </>
            ) : (
              <>
                <strong>{issuerUrl ? 'Check the requesting URL above' : 'Search for this import in your repo'}</strong>
                {` to determine whether the problematic import comes from your own code, or a dependency's code.`}
                {!issuerUrl &&
                  // chromium bug closed as wontfix: https://issues.chromium.org/issues/40241413
                  " (You can also try opening this page in Firefox and checking the console to see which package's " +
                    `bundle imported the bad path$; this isn't properly logged in Chromium due to a bug.)`}
                <ul>
                  <li>
                    <strong>If from your repo:</strong>{' '}
                    {isTrailingSlash ? (
                      'Remove the trailing slash from the import path.'
                    ) : isValidPackage ? (
                      <>
                        Import from the package root or a valid sub-path. (If you think the path is valid, verify that
                        the correct version of {prettyPackage} is listed as a dependency.)
                      </>
                    ) : (
                      'Make sure the package name is correct and listed as a dependency of the importing package.'
                    )}
                  </li>
                  <li>
                    <strong>If from a dependency:</strong>
                    {isTrailingSlash ? (
                      'Ask the owners to remove the trailing slash, or create a temporary patch in your repo.'
                    ) : (
                      <ul>
                        <li>
                          Verify that your local installation is up to date (run <code>yarn</code> or your repo&apos;s
                          package manager).
                        </li>
                        {/* If isValidPackage is false, obviously a dependency is missing. But also show this message
                        if the package is found in the repo, because there could be an implicit dependency resulting in
                        the wrong version being picked up. */}
                        <li>
                          Verify whether {prettyPackage} is listed in the importing package&apos;s dependencies. If not,
                          ask the owners to add it (or temporarily add the dependency in one of your own packages and
                          run <code>cloudpack init</code>).
                        </li>
                        <li>
                          If the dependency is listed, check whether any <code>resolutions</code> exist for{' '}
                          {prettyPackage} which might be forcing it to an incompatible version. If so, talk to your eng
                          sys team about removing or scoping the resolution.
                        </li>
                        <li>
                          If {prettyPackage} is in <code>peerDependencies</code> of the importing package, the peer may
                          be incorrectly satisfied in your dependency tree. Talk to your eng sys team for help.
                        </li>
                      </ul>
                    )}
                  </li>
                </ul>
              </>
            )
          }
        />
      </>
    );
  };

  return (
    <ResultsAccordion
      name={prettyImport}
      index={props.index}
      result="error"
      headerCommands={fixable && <Button onClick={addOverride}>Add override</Button>}
      getContent={getContent}
    />
  );
};
